import axios from 'axios';
import API from '../config/api';
import { getMetaContext } from './meta';

/**
 * Load the Razorpay Checkout SDK dynamically
 */
export const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export const verifyRazorpayPayment = async ({ orderId, orderType = 'order', guestBookingToken, response }) => {
  const result = await axios.post(
    `${API}/payments/razorpay/verify`,
    {
      razorpay_order_id: response.razorpay_order_id,
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_signature: response.razorpay_signature,
      order_id: orderId,
      order_type: orderType,
      meta: getMetaContext()
    },
    { withCredentials: true, headers: guestBookingToken ? { 'X-Guest-Booking-Token': guestBookingToken } : {} }
  );
  if (!result.data?.success) throw new Error(result.data?.detail || 'Payment verification failed');
  return result.data;
};

/**
 * Initiate Razorpay checkout payment flow
 */
export const processRazorpayPayment = async ({
  orderId,
  orderType = 'order',
  guestBookingToken,
  user,
  onSuccess,
  onError,
  onDismiss,
  onVerificationError
}) => {
  try {
    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      throw new Error('Razorpay SDK failed to load. Please check your internet connection.');
    }

    // 1. Create Razorpay order on backend
    const createRes = await axios.post(
      `${API}/payments/razorpay/create-order`,
      { order_id: orderId, order_type: orderType },
      { withCredentials: true, headers: guestBookingToken ? { 'X-Guest-Booking-Token': guestBookingToken } : {} }
    );

    const data = createRes.data;

    // 2. Configure Razorpay Options
    const options = {
      key: data.key_id || process.env.REACT_APP_RAZORPAY_KEY,
      amount: data.amount,
      currency: data.currency || 'INR',
      name: 'Paramparik Pooja',
      description: orderType === 'booking' ? 'Sacred Temple Puja Booking' : `Himalayan Order #${orderId.slice(0, 8)}`,
      image: 'https://images.unsplash.com/photo-1649147313351-c86537fda0eb?w=128&q=80',
      order_id: data.razorpay_order_id,
      prefill: {
        name: data.user_details?.name || user?.name || '',
        email: data.user_details?.email || user?.email || '',
        contact: data.user_details?.phone || user?.phone || ''
      },
      theme: {
        color: '#FF7B00'
      },
      handler: async function (response) {
        try {
          const verified = await verifyRazorpayPayment({ orderId, orderType, guestBookingToken, response });
          if (onSuccess) onSuccess({ ...verified, payment_id: response.razorpay_payment_id });
        } catch (verifyError) {
          console.error('Payment verification error:', verifyError);
          if (onVerificationError) onVerificationError(verifyError.response?.data?.detail || verifyError.message, response);
        }
      },
      modal: {
        ondismiss: function () {
          if (onDismiss) onDismiss();
        }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function (response) {
      console.error('Razorpay payment failed:', response.error);
      if (onError) onError(response.error.description || 'Payment transaction failed');
    });

    rzp.open();
  } catch (error) {
    console.error('Razorpay process error:', error);
    if (onError) onError(error.response?.data?.detail || error.message);
  }
};
